/** Adding a vendor and adding an account, in a side drawer - ADR-0212 (Add on the Vendors tables).
 *
 * The same two writes the register has always had (`POST /sources/vendors`, `POST /sources/accounts`),
 * in a drawer instead of an inline form. **An Approver's act**, because an account's dates narrow what
 * the census expects of every meter on it: the place offers Add to an Admin or an Approver only, and a
 * Contributor sees no control that would 403 (design rule 6).
 *
 * Three things the forms keep: a vendor has **no contact field** (a contact is a person, ADR-0062); an
 * account's reference is **kept as typed and never matched against a bill** (SEC-05); and an account's
 * start date **narrows every meter on it**, which the field says beside itself.
 */

import { useState } from "react";

import { useRecordAccount, useRecordVendor } from "../../api/sources";
import { WriteFailed } from "../../components/Loaded";
import { Drawer } from "../../components/ui/Drawer";
import styles from "./Vendors.module.css";

export function AddVendorDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const record = useRecordVendor();
  const [name, setName] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const close = () => {
    setName("");
    setRefusal(null);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Add vendor"
      width="sm"
      footer={
        <button
          type="submit"
          form="add-vendor"
          className={styles.primary}
          disabled={!name.trim() || record.isPending}
        >
          {record.isPending ? "Adding…" : "Add vendor"}
        </button>
      }
    >
      <form
        id="add-vendor"
        className={styles.form}
        data-testid="add-vendor"
        onSubmit={(event) => {
          event.preventDefault();
          setRefusal(null);
          record.mutateAsync({ name: name.trim() }).then(close, (error: unknown) => setRefusal(error));
        }}
      >
        <label className={styles.field}>
          <span>Name as it prints on a bill</span>
          <input
            value={name}
            required
            maxLength={200}
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <p className={styles.hint}>Your own note of who bills you. No contact is kept.</p>
        <WriteFailed error={refusal} of="the vendor register" onReload={() => setRefusal(null)} />
      </form>
    </Drawer>
  );
}

export function AddAccountDrawer({
  open,
  onClose,
  vendors,
  vendorId,
}: {
  open: boolean;
  onClose: () => void;
  vendors: ReadonlyArray<{ id: string; name: string }>;
  /** Preselects the vendor when the drawer is opened from a vendor. */
  vendorId?: string;
}) {
  const record = useRecordAccount();
  const [chosen, setChosen] = useState("");
  const [reference, setReference] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const vendor = chosen || vendorId || (vendors[0]?.id ?? "");
  const close = () => {
    setChosen("");
    setReference("");
    setFrom("");
    setTo("");
    setRefusal(null);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Add account"
      width="sm"
      footer={
        <button
          type="submit"
          form="add-account"
          className={styles.primary}
          disabled={!reference.trim() || !vendor || record.isPending}
        >
          {record.isPending ? "Adding…" : "Add account"}
        </button>
      }
    >
      {vendors.length === 0 ? (
        <p className={styles.hint} data-testid="add-account-needs-vendor">
          Add a vendor first. An account is billed by one.
        </p>
      ) : (
        <form
          id="add-account"
          className={styles.form}
          data-testid="add-account"
          onSubmit={(event) => {
            event.preventDefault();
            setRefusal(null);
            record
              .mutateAsync({
                vendor_id: vendor,
                account_ref: reference.trim(),
                operational_from: from || null,
                operational_to: to || null,
              })
              .then(close, (error: unknown) => setRefusal(error));
          }}
        >
          <label className={styles.field}>
            <span>Vendor</span>
            <select value={vendor} onChange={(event) => setChosen(event.target.value)}>
              {vendors.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span>Account reference</span>
            <input
              value={reference}
              required
              maxLength={255}
              autoComplete="off"
              onChange={(event) => setReference(event.target.value)}
            />
          </label>
          <p className={styles.hint}>Kept as typed. It is never matched against a bill.</p>
          <label className={styles.field}>
            <span>Began (optional)</span>
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <p className={styles.hint}>
            A start date stops anything before it being expected, for every meter on the account.
            Leave it blank unless the account really began then.
          </p>
          <label className={styles.field}>
            <span>Ended (optional)</span>
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
          <WriteFailed
            error={refusal}
            of="the register"
            testId="add-account-error"
            onReload={() => setRefusal(null)}
          />
        </form>
      )}
    </Drawer>
  );
}
